import { CatalystInsight, EarningsInsight, ThemeCompany } from '@api';
import { RevampTable, TableCell, TableHeader } from '@design/components/table';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys, queryValues } from '@terminal/routes/query-params';
import { useTranslation } from 'react-i18next';

import styles from './TopCompaniesTable.module.scss';
import {
  CATALYSTS_COLUMN_ID,
  RANK_COLUMN_ID,
  useTopCompaniesTableColumns,
} from './utils';

export interface ThemeCompanyWithInsights
  extends Pick<ThemeCompany, 'company'> {
  relationship: Pick<ThemeCompany['relationship'], 'rank'>;
  insights: CatalystInsight[];
  earningsInsight?: EarningsInsight;
}

export const TopCompaniesTable = ({
  data,
}: {
  data: ThemeCompanyWithInsights[];
}) => {
  const { t } = useTranslation(['widget']);
  const { setQueryParams } = useQueryParams();

  const columns = useTopCompaniesTableColumns();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className={styles.tableWrapper}>
      <RevampTable data-testid="companies-table">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const isCatalysts = header.id === CATALYSTS_COLUMN_ID;
                return (
                  <TableHeader
                    key={header.id}
                    className={styles.tableHeader}
                    isHoverEnabled={false}
                    alignment={isCatalysts ? 'end' : 'start'}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </TableHeader>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {!!data.length ? (
            table.getRowModel().rows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => {
                  const isCatalysts = cell.column.id === CATALYSTS_COLUMN_ID;
                  return (
                    <TableCell
                      key={cell.id}
                      className={styles.tableCell}
                      alignment={isCatalysts ? 'right' : 'left'}
                      isNumber={cell.column.id === RANK_COLUMN_ID}
                      onClick={() =>
                        setQueryParams({
                          [queryKeys.activeDrawer]:
                            queryValues.companyToThemeDrawer,
                          [queryKeys.entityTag]: row.original.company,
                        })
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  );
                })}
              </tr>
            ))
          ) : (
            <tr className={styles.emptyTableRow}>
              <td colSpan={columns.length}>
                <div className={styles.emptyState}>
                  <img
                    width={100}
                    height={100}
                    src="/app/static/images/icons/no-earnings.svg"
                    alt=""
                  />
                  <span className={styles.emptyStateText}>
                    {t('widget:themeOverview.tabs.overview.noData')}
                  </span>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </RevampTable>
    </div>
  );
};
